import { Grid, Box, Typography, CircularProgress, Button } from '@mui/material';
import { StorefrontOutlined, Add } from '@mui/icons-material';
import ShopCard from './ShopCard';
import AddShopCard from './AddShopCard';
import { Shop } from '../../types/shop.types';

interface ShopListProps {
  shops: Shop[];
  isLoading: boolean;
  canWrite: boolean;
  onEdit: (shop: Shop) => void;
  onDelete: (shop: Shop) => void;
  onViewHours: (shop: Shop) => void;
  onConfigureCoverage: (shop: Shop) => void;
  onAddShop: () => void;
}

export default function ShopList({ shops, isLoading, canWrite, onEdit, onDelete, onViewHours, onConfigureCoverage, onAddShop }: ShopListProps) {
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (shops.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 400,
          textAlign: 'center',
          py: 8,
          px: 3,
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: 96,
            height: 96,
            mb: 3,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.primary.main}18, ${theme.palette.primary.main}08)`,
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <StorefrontOutlined sx={{ fontSize: 42, color: 'primary.main', opacity: 0.8 }} />
          </Box>
        </Box>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
          Add your first shop
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400, lineHeight: 1.6 }}>
          Set up a shop location with operating hours and coverage needs to start building schedules.
        </Typography>
        {canWrite && (
          <Button
            variant="contained"
            disableElevation
            startIcon={<Add />}
            onClick={onAddShop}
            size="large"
            sx={{
              borderRadius: 50,
              textTransform: 'none',
              px: 4,
              py: 1.25,
              fontWeight: 600,
            }}
          >
            Create Shop
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      {shops.map((shop) => (
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={shop.id}>
          <ShopCard
            shop={shop}
            onEdit={onEdit}
            onDelete={onDelete}
            onViewHours={onViewHours}
            onConfigureCoverage={onConfigureCoverage}
          />
        </Grid>
      ))}
      {canWrite && (
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <AddShopCard onClick={onAddShop} disabled={isLoading} />
        </Grid>
      )}
    </Grid>
  );
}
